import { defineStore } from "pinia";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import * as jose from "jose";
import router from "@/router";
import { authModule } from "./auth";

/**
 * Signs the operator out the moment the access token expires, rather than
 * leaving a dead session on screen until the next request fails.
 *
 * The previous store armed its timer only from the login screen, so a page
 * refresh - the normal way an operator returns to an open tab - lost it, and the
 * session then sat there looking live until something was clicked. It also had no
 * cross-tab, visibility or wall-clock check, so a tab that was merely open while
 * the token ran out never noticed.
 *
 * Ported from horizon-logistics-admin, minus the session-extension prompt:
 * dna-express-api has no `/manager/refresh-token`, so there is nothing to extend
 * against.
 */

const REDIRECT_KEY = 'redirect'
const LOGOUT_REASON_KEY = 'logout_reason'

/** How often the backstop re-checks the clock, in ms. */
const BACKSTOP_INTERVAL = 60000

export const useAutoLogout = defineStore('autoLogout', {
  state: () => ({
    timerId: null,
    backstopTimerId: null,
    backstopIdleId: null,
    expMs: null, // epoch ms of token expiry
    crossTabSyncEnabled: false,
    visibilityCheckEnabled: false
  }),

  getters: {
    hasActiveSession: (s) => Boolean(s.expMs),
    msUntilExpiry: (s) => (s.expMs ? Math.max(0, s.expMs - Date.now()) : 0)
  },

  actions: {
    _clearTimer() {
      if (this.timerId) {
        clearTimeout(this.timerId)
        this.timerId = null
      }
    },

    _clearBackstop() {
      if (this.backstopTimerId) {
        clearTimeout(this.backstopTimerId)
        this.backstopTimerId = null
      }
      if (this.backstopIdleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.backstopIdleId)
        this.backstopIdleId = null
      }
    },

    /**
     * Read `exp` out of a JWT without verifying it.
     * @param {string} token - Raw JWT.
     * @returns {number|null} Expiry in epoch ms, or null if unreadable.
     */
    _decodeExpMs(token) {
      try {
        const decoded = jose.decodeJwt(token)
        return decoded?.exp ? decoded.exp * 1000 : null
      } catch (e) {
        return null
      }
    },

    /**
     * Where to send the operator back to once they sign in again.
     *
     * The router is imported rather than read off `this.$router`: the pinia
     * plugin assigns it onto the store, which is reactive, and a reactive proxy
     * unwraps the `currentRoute` ref on access - so `currentRoute.value` there is
     * undefined and every redirect was silently dropped.
     */
    _rememberRedirect() {
      const path = router.currentRoute.value?.fullPath
      if (path && path !== '/login') localStorage.setItem(REDIRECT_KEY, path)
      else localStorage.removeItem(REDIRECT_KEY)
    },

    /**
     * A modal or a Swal left open sits above the login form with its backdrop
     * still swallowing clicks, which reads as a frozen page rather than a
     * finished session.
     */
    _cleanupOpenUi() {
      try {
        Swal.close()
      } catch (e) {
        // A teardown failure must not stop the logout.
      }

      document.querySelectorAll('.modal.show').forEach((element) => {
        try {
          Modal.getInstance(element)?.hide()
        } catch (e) {
          // Same: best effort per modal.
        }
      })

      document.querySelectorAll('.modal-backdrop, .swal2-container').forEach((element) => element.remove())
      document.body.classList.remove('modal-open', 'swal2-shown', 'swal2-height-auto')
      document.body.style.removeProperty('padding-right')
      document.body.style.removeProperty('overflow')
    },

    /**
     * Sign out now.
     * @param {string} reason - 'expired' variants, 'manual', or 'remote_logout'.
     *   Stored so the login screen can say why, and read back exactly once.
     * @param {Object} [options]
     * @param {boolean} [options.preserveRedirect] - Whether to come back here
     *   after signing in. Defaults to true for everything but a manual logout.
     */
    logout(reason = 'expired', options = {}) {
      const { preserveRedirect = reason !== 'manual' } = options

      this.cancel()

      if (preserveRedirect) this._rememberRedirect()
      else localStorage.removeItem(REDIRECT_KEY)

      localStorage.setItem(LOGOUT_REASON_KEY, reason)
      this._cleanupOpenUi()

      // Clears the token, the cached user and the saved searches, and routes to
      // /login - the same path the topbar's Log out takes.
      authModule().logOut()
    },

    /**
     * A timer alone is not enough: a laptop suspended past the expiry wakes with
     * the timeout overdue and browsers throttle background tabs, so the clock is
     * also checked directly, on idle time where the browser offers it.
     */
    _startBackstop() {
      this._clearBackstop()

      const tick = () => {
        this.backstopTimerId = null
        this.backstopIdleId = null

        if (!this.expMs) return
        if (Date.now() >= this.expMs) return this.logout('expired_backstop')

        schedule()
      }

      const schedule = () => {
        if (typeof window.requestIdleCallback === 'function') {
          this.backstopIdleId = window.requestIdleCallback(tick, { timeout: BACKSTOP_INTERVAL })
        } else {
          this.backstopTimerId = setTimeout(tick, BACKSTOP_INTERVAL)
        }
      }

      schedule()
    },

    /**
     * Schedule the logout from the token in localStorage. Safe to call on app
     * boot, after login, and whenever the token is replaced.
     */
    scheduleFromStorage() {
      const token = localStorage.getItem('token')
      if (!token) return this.cancel()

      const expMs = this._decodeExpMs(token)
      // An unreadable token cannot authenticate anything, so treat it as expired
      // rather than leaving the session unmonitored.
      if (!expMs) return this.logout('expired')

      const delay = expMs - Date.now()
      if (delay <= 1000) return this.logout('expired_immediate')

      this._clearTimer()
      this._clearBackstop()

      this.expMs = expMs
      this.timerId = setTimeout(() => this.logout('expired_timeout'), delay)
      this._startBackstop()
    },

    /**
     * Reschedule against a replacement token.
     * @param {string} newToken - The token to store and schedule from.
     */
    rescheduleForToken(newToken) {
      if (newToken) localStorage.setItem('token', newToken)
      this.scheduleFromStorage()
    },

    /** Drop all timers without signing out. */
    cancel() {
      this._clearTimer()
      this._clearBackstop()
      this.expMs = null
    },

    /** Call once, on app boot. */
    initialize() {
      this.scheduleFromStorage()
      this.enableCrossTabSync()
      this.enableVisibilityCheck()
    },

    /** Signing out in one tab, or signing in as someone else, must reach the rest. */
    enableCrossTabSync() {
      if (this.crossTabSyncEnabled) return
      this.crossTabSyncEnabled = true

      window.addEventListener('storage', (event) => {
        if (event.key !== 'token') return
        if (!event.newValue) this.logout('remote_logout')
        else this.scheduleFromStorage()
      })
    },

    /** Catch a session that expired while the tab was hidden. */
    enableVisibilityCheck() {
      if (this.visibilityCheckEnabled) return
      this.visibilityCheckEnabled = true

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.expMs && Date.now() >= this.expMs) {
          this.logout('expired_visibility')
        }
      })
    }
  }
})

/**
 * Read and clear the reason for the last sign-out.
 * @returns {string} '' when the operator arrived at /login on their own.
 */
export const takeLogoutReason = () => {
  const reason = localStorage.getItem(LOGOUT_REASON_KEY) || ''
  localStorage.removeItem(LOGOUT_REASON_KEY)
  return reason
}
